import clsx from 'clsx'
import React, { PropsWithChildren, useCallback } from 'react'
import styled from 'styled-components'

import TextButton from 'components/Luxkit/Button/TextButton'
import AssistChip from 'components/Luxkit/Chips/AssistChip'
import LineAngleDownIcon from 'components/Luxkit/Icons/line/LineAngleDownIcon'
import LineCalendarAlertIcon from 'components/Luxkit/Icons/line/LineCalendarAlertIcon'
import LineCalendarCheckIcon from 'components/Luxkit/Icons/line/LineCalendarCheckIcon'
import LineCalendarPlusIcon from 'components/Luxkit/Icons/line/LineCalendarPlusIcon'
import Tooltip from 'components/Luxkit/Tooltip'
import { SHORT_DAY_NAME_DAY_MONTH_YEAR } from 'constants/dateFormats'
import noop from 'lib/function/noop'
import { preventDefault, stopPropagation } from 'lib/web/eventHandlerUtils'

const ItineraryChip = styled(AssistChip)`
  &:not(.in-itinerary) {
    gap: 0;
  }
`

interface Props {
  label?: string
  onClick?: () => void
  itineraryDate?: moment.Moment
  isProcessing?: boolean
  variant: 'chip' | 'button'
  withDropdownArrow: boolean
  hasWarning?: boolean
}

/**
 * For bookmarking something and adding to the itinerary at the same time
 */
const BaseBookmarkToItineraryButton = React.forwardRef<
  HTMLButtonElement,
  PropsWithChildren<Props>
>((props, ref) => {
  const {
    onClick = noop,
    itineraryDate,
    isProcessing = false,
    variant,
    withDropdownArrow,
    hasWarning = false,
  } = props

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      preventDefault(e)
      stopPropagation(e)

      onClick()
    },
    [onClick],
  )

  let startIcon
  if (hasWarning) {
    startIcon = <LineCalendarAlertIcon colour="warning" />
  } else if (itineraryDate) {
    startIcon = <LineCalendarCheckIcon colour="highlight-secondary-normal" />
  } else {
    startIcon = <LineCalendarPlusIcon />
  }

  return variant === 'button' ? (
    <TextButton
      ref={ref}
      aria-label={itineraryDate ? 'Remove from itinerary' : 'Add to itinerary'}
      kind="secondary"
      onClick={handleClick}
      disabled={isProcessing}
      size="large"
      startIcon={startIcon}
      endIcon={
        withDropdownArrow ? (
          <LineAngleDownIcon className="no-print" />
        ) : undefined
      }
    >
      {itineraryDate ?
        itineraryDate.format(SHORT_DAY_NAME_DAY_MONTH_YEAR) :
        'Add to itinerary'}
    </TextButton>
  ) : (
    <Tooltip
      description={itineraryDate ? 'Remove from itinerary' : 'Add to itinerary'}
    >
      <ItineraryChip
        ref={ref}
        aria-label={
          itineraryDate ? 'Remove from itinerary' : 'Add to itinerary'
        }
        className={clsx({ 'in-itinerary': !!itineraryDate })}
        variant="filled"
        size="small"
        onClick={handleClick}
        startIcon={startIcon}
        endIcon={
          withDropdownArrow ? (
            <LineAngleDownIcon className="no-print" />
          ) : undefined
        }
        disabled={isProcessing}
      >
        {itineraryDate ? 'Added' : undefined}
      </ItineraryChip>
    </Tooltip>
  )
})

BaseBookmarkToItineraryButton.displayName = 'BaseBookmarkToItineraryButton'

export default BaseBookmarkToItineraryButton
